import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import "photoswipe/dist/photoswipe.css";
import { Gallery, Item } from "react-photoswipe-gallery";
import { InStockProductModel } from "../../Api/Models/product.model";
import { riderService } from "../../Api/Services/RiderService";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { useLoading } from "../../Context/LoadingProvider";
import { RiderProfileLayoutContextInterface } from "./RiderProfileLayout";

interface CartItem {
  product: InStockProductModel;
  variation_id: number | null;
  quantity: number;
}

interface AssignKitlessGearProps {
  fetchGears: CallableFunction;
}

const stepButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: "30px",
  height: "30px",
  borderRadius: "8px",
  border: "none",
  background: disabled ? "#cbd5e1" : "#16a34a",
  color: "#fff",
  fontSize: "20px",
  lineHeight: "30px",
  textAlign: "center",
  padding: 0,
  flexShrink: 0,
  cursor: disabled ? "default" : "pointer",
});

const AssignKitlessGear: React.FunctionComponent<AssignKitlessGearProps> = ({
  fetchGears,
}) => {
  const { t } = useTranslation();
  const { showInfo } = useInfoModal();
  const { startLoading, finnishLoading } = useLoading();
  const { rider, setPageTitle, fetchRider } =
    useOutletContext<RiderProfileLayoutContextInterface>();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [results, setResults] = useState<InStockProductModel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [cart, setCart] = useState<CartItem[]>([]);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lazyLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setPageTitle(null);
  }, [t, setPageTitle]);

  useEffect(() => {
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(searchTerm, page);
    }, 600);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    //eslint-disable-next-line
  }, [searchTerm, page]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  useEffect(() => {
    const handleScroll = () => {
      if (lazyLoadingTimeoutRef.current) {
        clearTimeout(lazyLoadingTimeoutRef.current);
      }

      lazyLoadingTimeoutRef.current = setTimeout(() => {
        if (
          window.scrollY + window.innerHeight >=
          document.documentElement.scrollHeight
        ) {
          setPage((prev) => prev + 1);
        }
      }, 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (lazyLoadingTimeoutRef.current) {
        clearTimeout(lazyLoadingTimeoutRef.current);
      }
    };
    //eslint-disable-next-line
  }, []);

  const searchProducts = async (q: string, p: number = 0) => {
    setLoading(true);
    riderService
      .listInStockProducts(rider.riderId, q, p)
      .then((products) => {
        if (p === 0) {
          setResults(products);
        } else {
          setResults((prev) =>
            [...prev, ...products].filter(
              (value, index, self) =>
                self.findIndex((x) => x.id === value.id) === index
            )
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const stockOf = (
    product: InStockProductModel,
    variationId: number | null
  ): number =>
    variationId != null
      ? product.variations.find((v) => v.id === variationId)?.availableStock ??
        0
      : product.availableStock;

  const capFor = (product: InStockProductModel, variationId: number | null) => {
    const maxPerRider = product.maxPerRider ?? Infinity;
    const maxPerOrder = product.maxPerOrder ?? Infinity;
    return Math.min(stockOf(product, variationId), maxPerRider, maxPerOrder);
  };

  const addProduct = (product: InStockProductModel) => {
    setCart((prev) => {
      if (prev.some((c) => c.product.id === product.id)) {
        return prev;
      }
      const firstVariation = product.variations.find(
        (v) => v.availableStock > 0
      );
      const variation_id =
        product.variations.length > 0 ? firstVariation?.id ?? null : null;
      return [...prev, { product, variation_id, quantity: 1 }];
    });
  };

  const changeVariation = (index: number, variationId: number | null) => {
    setCart((prev) => {
      const next = [...prev];
      const item = next[index];
      const cap = capFor(item.product, variationId);
      next[index] = {
        ...item,
        variation_id: variationId,
        quantity: Math.max(1, Math.min(item.quantity, Math.max(1, cap))),
      };
      return next;
    });
  };

  const setQuantity = (index: number, value: number) => {
    setCart((prev) => {
      const next = [...prev];
      const item = next[index];
      const cap = capFor(item.product, item.variation_id);
      next[index] = {
        ...item,
        quantity: Math.max(1, Math.min(value, Math.max(1, cap))),
      };
      return next;
    });
  };

  const removeFromCart = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  const saveCart = () => {
    if (cart.length === 0) {
      return;
    }

    const missingSize = cart.find(
      (c) => c.product.variations.length > 0 && !c.variation_id
    );
    if (missingSize) {
      showInfo(t("Please select a size!"));
      return;
    }

    startLoading("assign-stock-gear");
    riderService
      .assignStockGear(
        rider.riderId,
        cart.map((c) => ({
          product_id: c.product.id,
          variation_id: c.variation_id,
          quantity: c.quantity,
        }))
      )
      .then(() => {
        setCart([]);
        showInfo(t("Gear successfully assigned!"));
        fetchGears();
        fetchRider();
      })
      .catch(() => {
        showInfo(t("Something went wrong. Please try again."));
      })
      .finally(() => {
        finnishLoading("assign-stock-gear");
      });
  };

  const cartProductIds = new Set(cart.map((c) => c.product.id));
  const visibleResults = results.filter(
    (p) => p.availableStock > 0 && !cartProductIds.has(p.id)
  );

  return (
    <>
      <p className="text-white text-center text-large mb-2">
        <b>{t("Assign Gear from Stock")}</b>
      </p>

      {/* Cart */}
      {cart.length > 0 && (
        <div className="card mb-4">
          <p className="text-center mb-3">
            <b>
              {t("Cart")} ({cartCount})
            </b>
          </p>

          {cart.map((item, index) => {
            const cap = capFor(item.product, item.variation_id);
            return (
              <div
                key={item.product.id}
                className="flex items-center"
                style={{
                  gap: "10px",
                  paddingBottom: "10px",
                  marginBottom: "10px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <img
                  src={item.product.image ?? ""}
                  alt={item.product.name}
                  style={{
                    width: "44px",
                    height: "44px",
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />

                <div
                  className="flex flex-col"
                  style={{ flexGrow: 1, minWidth: 0 }}
                >
                  <span style={{ fontWeight: 600 }}>{item.product.name}</span>
                  {item.product.variations.length > 0 && (
                    <select
                      className="input-field bg-lightgray"
                      style={{
                        marginTop: "4px",
                        height: "32px",
                        padding: "0 8px",
                        maxWidth: "140px",
                      }}
                      value={item.variation_id ?? ""}
                      onChange={(e) =>
                        changeVariation(
                          index,
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                    >
                      <option value="">-</option>
                      {item.product.variations.map((v) => (
                        <option
                          key={v.id}
                          value={v.id}
                          disabled={v.availableStock <= 0}
                        >
                          {v.name} ({v.availableStock})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div
                  className="flex items-center"
                  style={{ gap: "8px", flexShrink: 0 }}
                >
                  <button
                    type="button"
                    style={stepButtonStyle(item.quantity <= 1)}
                    disabled={item.quantity <= 1}
                    onClick={() => setQuantity(index, item.quantity - 1)}
                  >
                    −
                  </button>
                  <span style={{ minWidth: "20px", textAlign: "center" }}>
                    {item.quantity}
                  </span>
                  <button
                    type="button"
                    style={stepButtonStyle(item.quantity >= cap)}
                    disabled={item.quantity >= cap}
                    onClick={() => setQuantity(index, item.quantity + 1)}
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  className="modal__close"
                  style={{ flexShrink: 0 }}
                  onClick={() => removeFromCart(index)}
                >
                  ✖
                </button>
              </div>
            );
          })}

          <div className="flex justify-center mt-2">
            <button
              type="button"
              className="button button--green"
              onClick={saveCart}
            >
              {t("Assign")}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center">
        <input
          className="input-field text-blue w-60 see-through-background mb-2"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("Gear name") ?? ""}
        />

        <div
          className="flex flex-col"
          style={{ width: "100%", position: "relative", minHeight: "100px" }}
        >
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,.3)",
                zIndex: 99999999,
                borderRadius: "10px",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="loader mx-auto">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            </div>
          )}

          {!loading && visibleResults.length === 0 && (
            <p className="text-white text-center w-full">
              {t("No products in stock")}
            </p>
          )}

          {visibleResults.map((product) => (
            <div style={{ padding: "5px", width: "100%" }} key={product.id}>
              <div
                className="card flex items-center"
                style={{ cursor: "pointer", gap: "12px" }}
                onClick={() => addProduct(product)}
              >
                <Gallery>
                  <Item original={product.image ?? ""}>
                    {({ ref, open }) => (
                      <img
                        onClick={(e) => {
                          e.stopPropagation();
                          open(e);
                        }}
                        src={product.image ?? ""}
                        alt={product.name}
                        style={{
                          width: "56px",
                          height: "56px",
                          objectFit: "contain",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Item>
                </Gallery>

                <div className="flex flex-col" style={{ flexGrow: 1 }}>
                  <span>{product.name}</span>
                  <span className="text-sm">
                    {t("In stock")}: {product.availableStock}
                  </span>
                </div>

                <button
                  type="button"
                  className="button-add"
                  style={{ flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    addProduct(product);
                  }}
                >
                  <i className="icon-add"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AssignKitlessGear;
